// frontend/src/pages/referee/EvaluateOIT.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Footer from "../../components/Footer";
import api from "../../services/api";
import Swal from "sweetalert2";

// เพิ่ม CSS สำหรับการ์ดโรงเรียน และสถานะ
const uiStyles = `
  .school-card {
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
    border: 1px solid #dee2e6;
    border-left-width: 5px; /* เพิ่มขอบซ้ายสำหรับแสดงสถานะ */
  }
  .school-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important;
  }
  .school-card.card-selected {
    border-color: #0d6efd;
    box-shadow: 0 .5rem 1rem rgba(13,110,253,.25)!important;
    background-color: #f4f8ff;
  }
  .school-card .icon-default {
      color: #adb5bd;
  }
  .school-card.card-selected .icon-default {
      color: #0d6efd;
  }
  .editable-cell {
    cursor: pointer;
    transition: background-color 0.2s ease;
  }
  .editable-cell:hover {
    background-color: #f8f9fa;
  }
  /* Card Status Colors */
  .card-status-completed { border-left-color: #28a745; } /* สีเขียว */
  .card-status-rejected { border-left-color: #dc3545; } /* สีแดง */
  .card-status-pending { border-left-color: #ffc107; } /* สีเหลือง */

  .status-footer { font-size: 0.85rem; font-weight: bold; }
  .status-footer.completed { background-color: #28a745; color: white; }
  .status-footer.rejected { background-color: #dc3545; color: white; }
  .status-footer.pending { background-color: #ffc107; color: black; }
`;

const EvaluateOIT = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState("");
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState("");
  const [assignments, setAssignments] = useState([]);
  const [oitSubmissions, setOitSubmissions] = useState([]);
  const [activeTab, setActiveTab] = useState("list");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [score, setScore] = useState("");
  const [comment, setComment] = useState("");
  const [schoolStatuses, setSchoolStatuses] = useState({});

  const parseLinks = (linkString) => {
    if (!linkString) return [];
    return linkString.split("\n").filter((link) => link.trim()).map((link) => link.trim());
  };

  const viewAllLinks = (submission) => {
    const links = parseLinks(submission.link);
    if (links.length === 0) {
      Swal.fire({ icon: "info", title: "ไม่มีลิงก์", text: "ยังไม่มีการส่งลิงก์สำหรับตัวชี้วัดนี้", confirmButtonText: "ตกลง" });
      return;
    }
    let linksHtml = '<div class="list-group">';
    links.forEach((link, index) => {
      linksHtml += `<a href="${link}" target="_blank" rel="noopener noreferrer" class="list-group-item list-group-item-action"><div class="d-flex w-100 justify-content-between"><h6 class="mb-1">ลิงก์ที่ ${index + 1}</h6><small><i class="fas fa-external-link-alt"></i></small></div><p class="mb-1 text-break">${link}</p></a>`;
    });
    linksHtml += "</div>";
    Swal.fire({ title: `ลิงก์ทั้งหมด (${links.length} ลิงก์)`, html: linksHtml, confirmButtonText: "ปิด", width: "700px" });
  };

  useEffect(() => {
    const fetchFiscalYears = async () => {
      try {
        const response = await api.get("/fiscal-years");
        setFiscalYears(response.data);
        const activeYear = response.data.find((year) => year.active);
        if (activeYear) {
          setSelectedFiscalYear(activeYear.id.toString());
        } else if (response.data.length > 0) {
          setSelectedFiscalYear(response.data[0].id.toString());
        }
      } catch (error) {
        console.error("Error fetching fiscal years:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFiscalYears();
  }, []);

  useEffect(() => {
    if (selectedFiscalYear) {
      const fetchAssignments = async () => {
        setLoading(true);
        setSelectedSchool("");
        setOitSubmissions([]);
        setSchoolStatuses({});

        try {
          const response = await api.get(`/referee-assignments/referee/${user.id}/${selectedFiscalYear}`);
          setAssignments(response.data);
          const schoolsResponse = await api.get(`/schools-for-referee/${user.id}/${selectedFiscalYear}`);
          setSchools(schoolsResponse.data);
        } catch (error) {
          console.error("Error fetching assignments:", error);
          setSchools([]);
        } finally {
          setLoading(false);
        }
      };
      fetchAssignments();
    }
  }, [selectedFiscalYear, user.id]);

  useEffect(() => {
    if (schools.length > 0 && assignments.length > 0) {
        const fetchSchoolStatuses = async () => {
            const assignedQuestionIds = assignments.map(a => a.question_id);
            const statusPromises = schools.map(async (school) => {
                try {
                    const response = await api.get(`/oit/${school.id}/${selectedFiscalYear}`);
                    const submissions = response.data.filter(sub => assignedQuestionIds.includes(sub.question_id));
                    
                    if (!submissions || submissions.length === 0) {
                        return { schoolId: school.id, status: 'pending' };
                    }
                    if (submissions.some(sub => sub.status === 'rejected')) {
                        return { schoolId: school.id, status: 'rejected' };
                    }
                    if (submissions.every(sub => sub.evaluations?.some(e => e.referee_id === user.id))) {
                        return { schoolId: school.id, status: 'completed' };
                    }
                    return { schoolId: school.id, status: 'pending' };
                } catch (error) {
                    return { schoolId: school.id, status: 'pending' };
                }
            });

            const statuses = await Promise.all(statusPromises);
            const statusMap = statuses.reduce((acc, curr) => {
                acc[curr.schoolId] = curr.status;
                return acc;
            }, {});
            setSchoolStatuses(statusMap);
        };
        fetchSchoolStatuses();
    }
  }, [schools, assignments, selectedFiscalYear, user.id]);

  useEffect(() => {
    if (selectedSchool && selectedFiscalYear) {
        const fetchOITSubmissions = async () => {
            setLoading(true);
            try {
                const response = await api.get(`/oit/${selectedSchool}/${selectedFiscalYear}`);
                const assignedQuestionIds = assignments.map((a) => a.question_id);
                const filteredSubmissions = response.data.filter((submission) =>
                    assignedQuestionIds.includes(submission.question_id)
                );
                setOitSubmissions(filteredSubmissions);
            } catch (error) {
                setOitSubmissions([]);
            } finally {
                setLoading(false);
            }
        };
      fetchOITSubmissions();
    } else {
      setOitSubmissions([]);
    }
  }, [selectedSchool, selectedFiscalYear, assignments]);

  const refreshData = async () => {
    if (selectedSchool && selectedFiscalYear) {
        setLoading(true);
        const response = await api.get(`/oit/${selectedSchool}/${selectedFiscalYear}`);
        const assignedQuestionIds = assignments.map((a) => a.question_id);
        const filteredSubmissions = response.data.filter((submission) =>
            assignedQuestionIds.includes(submission.question_id)
        );
        setOitSubmissions(filteredSubmissions);
        setLoading(false);
    }
    const event = new Event('refreshStatuses');
    document.dispatchEvent(event);
  };
  
  useEffect(() => {
    const doRefresh = () => {
        if (schools.length > 0 && assignments.length > 0) {
            const fetchSchoolStatuses = async () => {
                const assignedQuestionIds = assignments.map(a => a.question_id);
                const statusPromises = schools.map(async (school) => {
                    try {
                        const response = await api.get(`/oit/${school.id}/${selectedFiscalYear}`);
                        const submissions = response.data.filter(sub => assignedQuestionIds.includes(sub.question_id));
                        if (!submissions || submissions.length === 0) return { schoolId: school.id, status: 'pending' };
                        if (submissions.some(sub => sub.status === 'rejected')) return { schoolId: school.id, status: 'rejected' };
                        if (submissions.every(sub => sub.evaluations?.some(e => e.referee_id === user.id))) return { schoolId: school.id, status: 'completed' };
                        return { schoolId: school.id, status: 'pending' };
                    } catch { return { schoolId: school.id, status: 'pending' }; }
                });
                const statuses = await Promise.all(statusPromises);
                const statusMap = statuses.reduce((acc, curr) => { acc[curr.schoolId] = curr.status; return acc; }, {});
                setSchoolStatuses(statusMap);
            };
            fetchSchoolStatuses();
        }
    };
    document.addEventListener('refreshStatuses', doRefresh);
    return () => document.removeEventListener('refreshStatuses', doRefresh);
  }, [schools, assignments, selectedFiscalYear, user.id]);

  const handleEvaluate = async (e) => {
    e.preventDefault();
    if (!selectedSubmission || !score) return;
    try {
      await api.post("/oit/evaluate", {oit_submission_id: selectedSubmission.id,referee_id: user.id,score: parseFloat(score),comment,});
      Swal.fire({ icon: "success", title: "สำเร็จ", text: "บันทึกการประเมินเรียบร้อยแล้ว" });
      await refreshData();
      setActiveTab("list");
    } catch (error) {
      Swal.fire({ icon: "error", title: "ข้อผิดพลาด", text: "ไม่สามารถบันทึกการประเมินได้" });
    }
  };
  const handleRejectOIT = async (submission) => {
    const { value: rejectionReason } = await Swal.fire({ title: 'ตีกลับการส่ง OIT', html: `<div class="mb-3 text-start"><label class="form-label">หัวข้อ: <strong>${submission.question_text}</strong></label></div><div class="mb-3 text-start"><label class="form-label">เหตุผลในการตีกลับ <span class="text-danger">*</span></label><textarea id="rejection-reason" class="form-control" rows="4" placeholder="กรุณาระบุเหตุผล"></textarea></div>`, showCancelButton: true, confirmButtonText: 'ตีกลับ', cancelButtonText: 'ยกเลิก', confirmButtonColor: '#dc3545', preConfirm: () => { const reason = document.getElementById('rejection-reason').value; if (!reason.trim()) { Swal.showValidationMessage('กรุณาระบุเหตุผล'); return false; } return reason; } });
    if (rejectionReason) {
      try {
        await api.post('/oit/reject', { oit_submission_id: submission.id, rejection_reason: rejectionReason, referee_id: user.id });
        Swal.fire({ icon: 'success', title: 'ตีกลับเรียบร้อย' });
        await refreshData();
      } catch (error) { Swal.fire({ icon: 'error', title: 'ข้อผิดพลาด', text: 'ไม่สามารถตีกลับได้' }); }
    }
  };
  const handleApproveOIT = async (submission) => {
    const result = await Swal.fire({ title: 'อนุมัติและให้คะแนนเต็ม', html: `<p>ต้องการอนุมัติและให้คะแนนเต็ม (100%) สำหรับข้อนี้หรือไม่?</p><p><strong>หัวข้อ:</strong> ${submission.question_text}</p>`, icon: 'question', showCancelButton: true, confirmButtonText: 'ยืนยัน', cancelButtonText: 'ยกเลิก', confirmButtonColor: '#28a745' });
    if (result.isConfirmed) {
      try {
        await api.post("/oit/evaluate", { oit_submission_id: submission.id, referee_id: user.id, score: 1.0, comment: "อนุมัติและให้คะแนนเต็มโดยอัตโนมัติ" });
        await api.post('/oit/approve', { oit_submission_id: submission.id });
        Swal.fire({ icon: 'success', title: 'สำเร็จ', text: 'อนุมัติและบันทึกคะแนนเต็มเรียบร้อยแล้ว' });
        await refreshData();
      } catch (error) { Swal.fire({ icon: 'error', title: 'ข้อผิดพลาด', text: 'ไม่สามารถดำเนินการได้' }); }
    }
  };
  
  // ฟังก์ชันใหม่สำหรับยกเลิกการอนุมัติ
  const handleCancelApproval = async (submission) => {
    const result = await Swal.fire({
      title: 'ยืนยันการยกเลิกอนุมัติ',
      html: `
        <p>คุณต้องการยกเลิกการอนุมัติสำหรับรายการนี้ใช่หรือไม่</p>
        <p><strong>หัวข้อ:</strong> ${submission.question_text}</p>
        <p class="text-danger">สถานะจะถูกเปลี่ยนกลับเป็น <strong>รอการตรวจสอบ</strong> และคะแนน 100% ที่เคยให้ไว้จะถูกลบ</p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ใช่, ยกเลิกการอนุมัติ',
      confirmButtonColor: '#dc3545',
      cancelButtonText: 'ไม่',
    });

    if (result.isConfirmed) {
      try {
        await api.post('/oit/cancel-approval', {
          oit_submission_id: submission.id,
          referee_id: user.id
        });

        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'ยกเลิกการอนุมัติเรียบร้อยแล้ว'
        });

        await refreshData();
      } catch (error) {
        console.error('Error canceling approval:', error);
        Swal.fire({
          icon: 'error',
          title: 'ข้อผิดพลาด',
          text: error.response?.data?.message || 'ไม่สามารถยกเลิกการอนุมัติได้'
        });
      }
    }
  };


  const handleEditScoreClick = (submission, evaluation, links) => {
    if (links.length === 0) { Swal.fire({ icon: 'warning', title: 'ไม่สามารถประเมินได้', text: 'ไม่มีลิงก์ที่ส่งมา' }); return; }
    setSelectedSubmission(submission);
    if (evaluation) { setScore(evaluation.score.toString()); setComment(evaluation.comment || ''); } else { setScore(''); setComment(''); }
    setActiveTab('evaluate');
  };

  return (
    <div className="wrapper">
      <style>{uiStyles}</style>
      <Header user={user} title="ประเมิน OIT" />
      <Sidebar user={user} />
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid"><div className="row mb-2"><div className="col-sm-6"><h1 className="m-0">ประเมิน OIT</h1></div><div className="col-sm-6"><ol className="breadcrumb float-sm-right"><li className="breadcrumb-item"><Link to="/referee/dashboard">หน้าหลัก</Link></li><li className="breadcrumb-item active">ประเมิน OIT</li></ol></div></div></div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="card card-primary card-outline">
              <div className="card-header"><h3 className="card-title"><i className="fas fa-calendar-alt me-2"></i>เลือกปีงบประมาณ</h3></div>
              <div className="card-body">
                <div className="form-group">
                  <label>ปีงบประมาณ</label>
                  <select className="form-control" value={selectedFiscalYear} onChange={(e) => setSelectedFiscalYear(e.target.value)}>
                    <option value="">-- เลือกปีงบประมาณ --</option>
                    {fiscalYears.map((year) => (<option key={year.id} value={year.id}>{year.year} {year.active && "(ปีปัจจุบัน)"}</option>))}
                  </select>
                </div>
              </div>
            </div>

            {selectedFiscalYear && (
              <div className="card card-primary card-outline">
                <div className="card-header"><h3 className="card-title"><i className="fas fa-school me-2"></i>เลือกโรงเรียนที่ต้องการประเมิน</h3></div>
                <div className="card-body">
                  {loading && schools.length === 0 && <p>กำลังโหลดรายชื่อโรงเรียน...</p>}
                  {!loading && schools.length > 0 ? (
                    <div className="row row-cols-1 row-cols-sm-2 row-cols-md-4 row-cols-lg-5 row-cols-xxl-10 g-3">
                      {schools.map((school) => {
                        const status = schoolStatuses[school.id] || 'pending';
                        return (
                          <div key={school.id} className="col">
                            <div className={`card h-100 text-center school-card card-status-${status} ${selectedSchool === school.id.toString() ? 'card-selected' : ''}`}
                              onClick={() => { setSelectedSchool(school.id.toString()); setTimeout(() => { document.getElementById('evaluation-section')?.scrollIntoView({ behavior: 'smooth' }); }, 100); }}>
                              <div className="card-body d-flex flex-column justify-content-center align-items-center">
                                <i className="fas fa-school fa-3x mb-3 icon-default"></i>
                                <h5 className="card-title" style={{fontSize: '0.9rem'}}>{school.name}</h5>
                              </div>
                              <div className={`card-footer status-footer ${status}`}>
                                {status === 'completed' && <><i className="fas fa-check-circle me-1"></i>ประเมินครบถ้วน</>}
                                {status === 'rejected' && <><i className="fas fa-exclamation-triangle me-1"></i>มีรายการตีกลับ</>}
                                {status === 'pending' && <><i className="fas fa-hourglass-half me-1"></i>รอประเมิน</>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    !loading && <div className="alert alert-warning mt-3"><i className="fas fa-exclamation-triangle me-2"></i>ไม่พบโรงเรียนที่ท่านมีสิทธิ์ประเมินในปีงบประมาณนี้</div>
                  )}
                </div>
              </div>
            )}

            {selectedSchool && (
              <div id="evaluation-section" className="card card-primary card-outline">
                <div className="card-header"><h3 className="card-title"><i className="fas fa-clipboard-check me-2"></i>รายการประเมิน OIT ของ: {schools.find(s => s.id.toString() === selectedSchool)?.name}</h3></div>
                <div className="card-body">
                  <ul className="nav nav-tabs" id="custom-tabs-one-tab" role="tablist">
                    <li className="nav-item"><a className={`nav-link ${activeTab === "list" ? "active" : ""}`} onClick={() => setActiveTab("list")} href="#/" role="tab"><i className="fas fa-list me-1"></i>รายการข้อมูล OIT</a></li>
                    {selectedSubmission && (<li className="nav-item"><a className={`nav-link ${activeTab === "evaluate" ? "active" : ""}`} onClick={() => setActiveTab("evaluate")} href="#/" role="tab"><i className="fas fa-edit me-1"></i>ประเมินข้อมูล OIT</a></li>)}
                  </ul>
                  <div className="tab-content mt-3" id="custom-tabs-one-tabContent">
                    {activeTab === "list" && (
                      <div className="tab-pane fade show active" role="tabpanel">
                        {loading && oitSubmissions.length === 0 ? (
                          <div className="text-center p-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table table-bordered table-hover">
                              <thead>
                                <tr className="bg-light">
                                  <th style={{ width: "5%" }} className="text-center">#</th>
                                  <th>หัวข้อการประเมิน และ สถานะ</th>
                                  <th style={{ width: "15%" }} className="text-center">ลิงก์ที่ส่ง</th>
                                  <th style={{ width: "15%" }} className="text-center">คะแนนของฉัน (คลิกเพื่อแก้ไข)</th>
                                  <th style={{ width: "15%" }} className="text-center">ดำเนินการ</th>
                                </tr>
                              </thead>
                              <tbody>
                                {oitSubmissions.length > 0 ? (
                                  oitSubmissions.map((submission, index) => {
                                    const evaluation = submission.evaluations?.find((e) => e.referee_id === user.id);
                                    const links = parseLinks(submission.link);
                                    return (
                                      <tr key={submission.id} className={submission.status === 'rejected' ? 'table-warning' : ''}>
                                        <td className="text-center align-middle">{index + 1}</td>
                                        <td className="align-middle"><strong>{submission.question_text}</strong><div className="mt-1">{submission.status === 'pending' && <span className="badge bg-warning">รอการตรวจสอบ</span>}{submission.status === 'approved' && <span className="badge bg-success">อนุมัติแล้ว</span>}{submission.status === 'rejected' && <span className="badge bg-danger">ตีกลับแล้ว</span>}</div>{submission.status === 'rejected' && submission.rejection_reason && <div className="mt-2"><div className="alert alert-warning p-2 mb-0"><small><strong>เหตุผลการตีกลับ:</strong> {submission.rejection_reason}</small></div></div>}{links.length > 1 && <div className="mt-1"><span className="badge bg-info"><i className="fas fa-link me-1"></i>{links.length} ลิงก์</span></div>}</td>
                                        <td className="text-center align-middle">{links.length > 0 ? (<div className="btn-group-vertical btn-group-sm">{links.length === 1 ? <a href={links[0]} target="_blank" rel="noopener noreferrer" className="btn btn-info btn-sm"><i className="fas fa-external-link-alt me-1"></i> เปิดลิงก์</a> : <button className="btn btn-info btn-sm" onClick={() => viewAllLinks(submission)}><i className="fas fa-eye me-1"></i>ดูลิงก์ทั้งหมด ({links.length})</button>}</div>) : <span className="badge bg-danger">ไม่มีลิงก์</span>}</td>
                                        <td className="text-center align-middle editable-cell" onClick={() => handleEditScoreClick(submission, evaluation, links)} title="คลิกเพื่อประเมินหรือแก้ไขคะแนน">{evaluation ? (<><span className={`badge ${parseFloat(evaluation.score) >= 0.75 ? "bg-success" : parseFloat(evaluation.score) >= 0.5 ? "bg-warning" : "bg-danger"} p-2`}>{(parseFloat(evaluation.score) * 100).toFixed(0)}%</span><div className="small text-muted mt-1"><i className="fas fa-edit me-1"></i>แก้ไข</div></>) : (<><span className="badge bg-secondary p-2">ยังไม่ได้ประเมิน</span>{links.length > 0 && <div className="small text-muted mt-1"><i className="fas fa-edit me-1"></i>ประเมิน</div>}</>)}</td>
                                        <td className="text-center align-middle">
                                            <div className="btn-group-vertical btn-group-sm w-100">
                                                {links.length > 0 && submission.status !== 'approved' && (
                                                    <button className="btn btn-success btn-sm mb-1" onClick={() => handleApproveOIT(submission)}>
                                                        <i className="fas fa-check me-1"></i> อนุมัติและให้คะแนนเต็ม
                                                    </button>
                                                )}
                                                {links.length > 0 && submission.status === 'approved' && (
                                                    <button className="btn btn-warning btn-sm mb-1" onClick={() => handleCancelApproval(submission)}>
                                                        <i className="fas fa-undo me-1"></i> ยกเลิกการอนุมัติ
                                                    </button>
                                                )}
                                                {links.length > 0 && submission.status !== 'rejected' && submission.status !== 'approved' && (
                                                    <button className="btn btn-danger btn-sm" onClick={() => handleRejectOIT(submission)}>
                                                        <i className="fas fa-times me-1"></i> ตีกลับ
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr><td colSpan="5" className="text-center"><div className="py-4"><i className="fas fa-inbox fa-3x text-muted mb-3"></i><p>ไม่พบข้อมูล OIT ที่ท่านมีสิทธิ์ประเมินสำหรับโรงเรียนนี้</p></div></td></tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === 'evaluate' && selectedSubmission && (
                        <div className="tab-pane fade show active" role="tabpanel">
                            <div className="p-3">
                                <h5><i className="fas fa-clipboard-check me-2"></i>ประเมินข้อมูล OIT</h5>
                                <div className="card bg-light mb-4">
                                <div className="card-body">
                                    <h6 className="card-title"><i className="fas fa-question-circle me-2"></i>หัวข้อการประเมิน</h6>
                                    <p className="card-text"><strong>{selectedSubmission.question_text}</strong></p>
                                    <h6 className="card-title mt-3"><i className="fas fa-link me-2"></i>ลิงก์ที่ส่งมา ({parseLinks(selectedSubmission.link).length} ลิงก์)</h6>
                                    {parseLinks(selectedSubmission.link).length > 0 ? (
                                    <div className="list-group">
                                        {parseLinks(selectedSubmission.link).map(
                                        (link, index) => (
                                            <a key={index} href={link} target="_blank" rel="noopener noreferrer" className="list-group-item list-group-item-action">
                                            <div className="d-flex w-100 justify-content-between">
                                                <h6 className="mb-1">ลิงก์ที่ {index + 1}</h6>
                                                <small><i className="fas fa-external-link-alt"></i></small>
                                            </div>
                                            <p className="mb-1 text-break small">{link}</p>
                                            </a>
                                        )
                                        )}
                                    </div>
                                    ) : ( <div className="alert alert-warning"><i className="fas fa-exclamation-triangle me-2"></i>ไม่มีลิงก์ที่ส่งมา</div>)}
                                </div>
                                </div>
                                <form onSubmit={handleEvaluate}>
                                <div className="card">
                                    <div className="card-header bg-primary text-white"><h6 className="mb-0"><i className="fas fa-star me-2"></i>การให้คะแนน</h6></div>
                                    <div className="card-body">
                                    <div className="form-group mb-3">
                                        <label className="form-label">คะแนน <span className="text-danger">*</span></label>
                                        <select className="form-select" value={score} onChange={(e) => {setScore(e.target.value);}} required>
                                        <option value="">-- เลือกคะแนน --</option>
                                        <option value="0">0% - ไม่มีข้อมูล</option>
                                        <option value="0.5">50% - มีข้อมูล แต่ไม่ครบถ้วน</option>
                                        <option value="1">100% - มีข้อมูลครบถ้วน</option>
                                        </select>
                                        <div className="form-text"><i className="fas fa-info-circle me-1"></i>ประเมินจากลิงก์ทั้งหมดที่ส่งมา</div>
                                    </div>
                                    <div className="form-group mb-3">
                                        <label className="form-label">ข้อคิดเห็น</label>
                                        <textarea className="form-control" rows="4" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="ข้อคิดเห็นเพิ่มเติม เช่น ข้อมูลที่ขาดหายไป หรือข้อเสนอแนะ"></textarea>
                                    </div>
                                    </div>
                                </div>
                                <div className="d-flex justify-content-between mt-4">
                                    <button type="button" className="btn btn-secondary" onClick={() => { setActiveTab("list"); setSelectedSubmission(null); setScore(""); setComment(""); }}><i className="fas fa-arrow-left me-1"></i>ย้อนกลับ</button>
                                    <button type="submit" className="btn btn-primary"><i className="fas fa-save me-1"></i>บันทึกการประเมิน</button>
                                </div>
                                </form>
                            </div>
                        </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default EvaluateOIT;